import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { baseAxios } from "../../apis";
import { useToast } from "../../context/toastContext";
import { extractErrorMessage } from "../../utils";
import moment from "moment";
import { useCsrfToken } from "../../hooks/useCsrfToken";
import { useSidebar } from "../../context/sidebarContext";
import ConfirmationModal from "./ConfirmationModal";
import TradeHistoryTable from "./TradeHistoryTable";
import useAuthedApi from "../../hooks/useAuthedApi";

// The delete mutation hook remains here as it's tied to this component's UI.
const useDeleteOrderMutation = () => {
  const { authedDelete } = useAuthedApi(); // Get the authedDelete function
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderId) => {
      // The mutation function is now a simple, single line
      return authedDelete("/api/paper_trading/order/", {
        params: { order_id: orderId },
      });
    },
    onSuccess: () => {
      addToast("success", "Order successfully canceled.");
      queryClient.invalidateQueries({ queryKey: ["paperTradingOrders"] });
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error?.response?.data);
      addToast("error", errorMessage, 10000);
    },
  });
};

export const TradeTableOrderHistory = ({
  openOrders,
  openTotalPages,
  loadingOpen,
  openPage,
  setOpenPage,
  filledOrders,
  filledTotalPages,
  loadingFilled,
  filledPage,
  setFilledPage,
}) => {
  const { isMobile } = useSidebar();

  // UI-related state is still managed here.
  const [activeTab, setActiveTab] = useState("Open Order");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const deleteOrderMutation = useDeleteOrderMutation();

  const handleDeleteClick = (orderId) => {
    setOrderToDelete(orderId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (deleteOrderMutation.isPending) return;
    setIsModalOpen(false);
    setOrderToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!orderToDelete) return;
    deleteOrderMutation.mutate(orderToDelete, {
      onSuccess: () => handleCloseModal(),
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setExpandedRow(null);
  };

  const toggleRowExpansion = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  // Derive variables from props based on the active tab.
  const isOpening = activeTab === "Open Order";
  const displayedOrders = isOpening ? openOrders : filledOrders;
  const totalPages = isOpening ? openTotalPages : filledTotalPages;
  const loading = isOpening ? loadingOpen : loadingFilled;
  const currentPage = isOpening ? openPage : filledPage;
  const setCurrentPage = isOpening ? setOpenPage : setFilledPage;

  return (
    <div className="mt-5">
      <TradeHistoryTable
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        data={displayedOrders}
        loading={loading}
        onDeleteClick={handleDeleteClick}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        toggleRowExpansion={toggleRowExpansion}
        expandedRow={expandedRow}
        isMobile={isMobile}
      />
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        loading={deleteOrderMutation.isPending}
      />
    </div>
  );
};
